import api from './api';
import { CURRENCY } from '../config/currency';

// ========================
// Types for Invoice Timeline API
// ========================

export interface FloorSummary {
  id: number;
  name: string;
  floorNumber: number | null;
  tableCount: number;
  activeSessionCount: number;
  totalRevenue: number;
  totalSessionCount: number;
}

export interface SessionSummary {
  id: number;
  sessionCode: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  totalAmount: number;
  paidAmount: number;
  paymentStatus: string;
  orderCount: number;
  customer: CustomerInfo | null;
}

export interface TableSummary {
  id: number;
  name: string;
  displayNumber: string;
  tableNumber: number | null;
  hasActiveSession: boolean;
  sessionCount: number;
  totalRevenue: number;
  averageSessionValue: number;
  activeSession: SessionSummary | null;
}

export interface CustomerInfo {
  id: number | null;
  name: string;
  phone: string | null;
  type: string;
}

export interface TimelineOrderSummary {
  id: number;
  customOrderId: string;
  status: string;
  total: number;
  createdAt: string;
  itemCount: number;
}

export interface SessionTimeline {
  id: number;
  sessionCode: string;
  startedAt: string;
  endedAt: string | null;
  durationMinutes: number | null;
  totalAmount: number;
  paymentStatus: string;
  customerType: string | null;
  customerName: string | null;
  orders: TimelineOrderSummary[];
}

export interface DateRangeParams {
  startDate?: string; // ISO timestamp
  endDate?: string; // ISO timestamp
}

export interface DailySummary {
  date: string; // ISO date
  sessionCount: number;
  orderCount: number;
  totalRevenue: number;
  averageSessionValue: number;
  activeSessionCount: number;
}

export interface WeeklySummary {
  weekStart: string; // ISO date
  weekEnd: string; // ISO date
  weekNumber: number;
  sessionCount: number;
  orderCount: number;
  totalRevenue: number;
  averageSessionValue: number;
  peakDaySessionCount: number;
  peakDay: string | null; // ISO date
}

export type AggregationTier = 'HOURLY' | 'DAILY' | 'WEEKLY';

export interface AggregationTierResponse {
  tier: AggregationTier;
  days: number;
  recommendation: string;
}

// Export job types
export type ExportJobType = 'INVOICE_EXPORT' | 'SESSION_EXPORT' | 'ORDER_EXPORT';
export type ExportJobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface ExportJob {
  id: number;
  jobType: ExportJobType;
  status: ExportJobStatus;
  startDate: string;
  endDate: string;
  totalRecords: number | null;
  processedRecords: number | null;
  progressPercent: number | null;
  fileUrl: string | null;
  fileName: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

// ========================
// Invoice Timeline API Functions
// ========================

/**
 * Get floor summaries with session statistics for the authenticated vendor.
 * @param params Date range parameters (optional, defaults to today)
 */
export async function getFloorSummaries(params?: DateRangeParams): Promise<FloorSummary[]> {
  const response = await api.get('/invoices/table-orders/floors', { params });
  return response.data;
}

/**
 * Get table summaries for a specific floor.
 * @param floorId The floor ID
 * @param params Date range parameters (optional, defaults to today)
 */
export async function getTableSummariesForFloor(
  floorId: number,
  params?: DateRangeParams
): Promise<TableSummary[]> {
  const response = await api.get(`/invoices/table-orders/floors/${floorId}/tables`, { params });
  return response.data;
}

/**
 * Get session timeline for a specific table.
 * @param tableId The table ID
 * @param params Date range parameters (optional, defaults to today)
 */
export async function getSessionTimeline(
  tableId: number,
  params?: DateRangeParams
): Promise<SessionTimeline[]> {
  const response = await api.get(`/invoices/table-orders/tables/${tableId}/sessions`, { params });
  return response.data;
}

/**
 * Get detailed information for a specific session.
 * @param sessionId The session (OrderLine) ID
 */
export async function getSessionDetail(sessionId: number): Promise<SessionTimeline> {
  const response = await api.get(`/invoices/table-orders/sessions/${sessionId}`);
  return response.data;
}

/**
 * Get daily summaries for the authenticated vendor.
 * @param params Date range parameters (7-30 days recommended)
 */
export async function getDailySummaries(params: DateRangeParams): Promise<DailySummary[]> {
  const response = await api.get('/invoices/table-orders/daily-summaries', { params });
  return response.data;
}

/**
 * Get weekly summaries for the authenticated vendor.
 * @param params Date range parameters (30+ days recommended)
 */
export async function getWeeklySummaries(params: DateRangeParams): Promise<WeeklySummary[]> {
  const response = await api.get('/invoices/table-orders/weekly-summaries', { params });
  return response.data;
}

/**
 * Get the recommended aggregation tier for a date range.
 * @param params Date range parameters
 */
export async function getAggregationTier(params: DateRangeParams): Promise<AggregationTierResponse> {
  const response = await api.get('/invoices/table-orders/aggregation-tier', { params });
  return response.data;
}

// ========================
// Export API Functions
// ========================

/**
 * Start a new export job.
 * @param params Date range and job type parameters
 */
export async function startExport(params: {
  startDate: string;
  endDate: string;
  jobType?: ExportJobType;
}): Promise<ExportJob> {
  const response = await api.post('/invoices/table-orders/exports', null, { params });
  return response.data;
}

/**
 * Get export job status.
 * @param jobId The job ID
 */
export async function getExportStatus(jobId: number): Promise<ExportJob> {
  const response = await api.get(`/invoices/table-orders/exports/${jobId}`);
  return response.data;
}

/**
 * Get recent export jobs for the authenticated vendor.
 */
export async function getRecentExports(): Promise<ExportJob[]> {
  const response = await api.get('/invoices/table-orders/exports');
  return response.data;
}

/**
 * Cancel an export job.
 * @param jobId The job ID
 */
export async function cancelExport(jobId: number): Promise<void> {
  await api.delete(`/invoices/table-orders/exports/${jobId}`);
}

/**
 * Calculate the number of days between two dates.
 */
export function getDaysBetween(startDate: Date, endDate: Date): number {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Determine the aggregation tier based on date range (client-side).
 */
export function determineAggregationTier(startDate: Date, endDate: Date): AggregationTier {
  const days = getDaysBetween(startDate, endDate);
  if (days <= 7) return 'HOURLY';
  if (days <= 30) return 'DAILY';
  return 'WEEKLY';
}

// ========================
// Helper Functions
// ========================

/**
 * Convert Date objects to ISO timestamp strings for API params.
 */
export function toDateRangeParams(startDate: Date, endDate: Date): DateRangeParams {
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  };
}

/**
 * Format currency value for display.
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(CURRENCY.locale, {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format duration in minutes to human-readable string.
 */
export function formatDuration(minutes: number | null): string {
  if (minutes === null) return 'Active';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
}

/**
 * Get payment status display text and color.
 */
export function getPaymentStatusInfo(status: string): { text: string; color: string } {
  switch (status) {
    case 'PAID':
      return { text: 'Paid', color: '#4CAF50' };
    case 'PARTIAL':
      return { text: 'Partial', color: '#FF9800' };
    case 'UNPAID':
      return { text: 'Unpaid', color: '#F44336' };
    case 'BILL_REQUESTED':
      return { text: 'Bill Requested', color: '#2196F3' };
    case 'PENDING':
      return { text: 'Active', color: '#9E9E9E' };
    default:
      return { text: status, color: '#757575' };
  }
}

export default {
  getFloorSummaries,
  getTableSummariesForFloor,
  getSessionTimeline,
  getSessionDetail,
  getDailySummaries,
  getWeeklySummaries,
  getAggregationTier,
  startExport,
  getExportStatus,
  getRecentExports,
  cancelExport,
  toDateRangeParams,
  formatCurrency,
  formatDuration,
  getPaymentStatusInfo
};
